import React, {FC, MouseEventHandler, useRef, useState} from 'react';
import {
  COLORS,
  FONT_SIZES,
  FONT_WEIGHTS,
  useOnClickOutside,
} from '@aboutyou/ui-components';
import {useTranslation} from '@aboutyou/i18n';
import {LanguageCountrySwitchFlyout} from './LanguageCountrySwitchFlyout';
import styled, {css} from 'styled-components';
import {CountryFlag} from './shared';
import {lazy} from '@aboutyou/lazy-imports';

export const LanguageCountrySwitchPopup = lazy(() =>
  import('./LanguageCountrySwitchPopup').then(mod => ({
    default: mod.LanguageCountrySwitchPopup,
  })),
);

type CurrentSelectionProps = {
  selectedCountryFlagSrc?: string;
  selectedCountryLabel?: string;
  selectedLanguageLabel?: string;
};

export type Languages = Array<{
  isSelected: boolean;
  label: string;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  linkTarget?: string;
}>;

export type Countries = Array<{
  flagSrc?: string;
  isSelected: boolean;
  label: string;
  linkTarget?: string;
}>;

export type SelectOptionsProps = {
  countries: Countries;
  languages?: Languages;
};

export type LanguageCountrySwitchProps = CurrentSelectionProps &
  SelectOptionsProps & {
    className?: string;
    labelAndBorderColor?: COLORS;
    openAs: 'flyout' | 'popup';
    onHover?: (status: boolean) => void;
    overlayWrapperZIndex?: number;
    shouldUseBorderOnHover?: boolean;
    shouldDisplayArrowOnHover?: boolean;
  };

export const LanguageCountrySwitch: FC<LanguageCountrySwitchProps> = ({
  className,
  countries,
  labelAndBorderColor,
  languages,
  openAs,
  overlayWrapperZIndex,
  selectedCountryFlagSrc,
  selectedCountryLabel,
  selectedLanguageLabel,
  shouldUseBorderOnHover = true,
  shouldDisplayArrowOnHover = false,
  onHover,
}) => {
  const ref = useRef(null);

  // Open state will be triggered by hover, but click "sets" open state even if not hovered.
  // This is especially relevant for touch devices.
  const [
    isLanguageSwitchComponentHovered,
    setIsLanguageSwitchComponentHovered,
  ] = useState(false);
  const [forceOpenPopup, setForceOpenPopup] = useState(false);
  const [flyoutOpen, setFlyoutOpen] = useState(false);

  useOnClickOutside(ref, () => {
    // We do not need this click if we are in flyout mode as the flyout will be closed by the flyout itself
    if (openAs === 'popup') {
      setForceOpenPopup(false);

      handleVisibilityState(false);
    }
  });

  const longSelectedCountryLabel = countries.find(c => c.isSelected)?.label;

  const shouldShowPopup =
    (isLanguageSwitchComponentHovered || forceOpenPopup) && openAs === 'popup';

  const handleVisibilityState = (open: boolean) => {
    setIsLanguageSwitchComponentHovered(open);
    onHover?.(open);
  };

  const {t} = useTranslation();

  return (
    <Wrapper
      onMouseEnter={() => handleVisibilityState(true)}
      onMouseLeave={() => handleVisibilityState(false)}
      $forceOpen={forceOpenPopup}
      $popupEnabled={openAs === 'popup'}
      $shouldUseBorder={shouldUseBorderOnHover}
      $shouldDisplayArrowOnHover={shouldDisplayArrowOnHover}
      $overlayWrapperZIndex={overlayWrapperZIndex}
      ref={ref}
      role="region"
      aria-label={t('Language and country switch')}
    >
      <Switch
        $shouldUseBorder={shouldUseBorderOnHover}
        className={className}
        data-testid="languageCountrySwitch"
        onClick={() =>
          openAs === 'flyout'
            ? setFlyoutOpen(prev => !prev)
            : setForceOpenPopup(prev => !prev)
        }
      >
        <>
          <SelectedCountry>
            {selectedCountryFlagSrc && (
              <SelectedCountryFlag
                data-testid={`languageCountrySwitchSelectedCountryFlag`}
                height="11"
                src={selectedCountryFlagSrc}
                width="13"
                alt={`${longSelectedCountryLabel} ${t('flag')}`}
              />
            )}
            {selectedCountryLabel && (
              <SelectedCountryLabel
                data-testid={`languageCountrySwitchSelectedCountry_${selectedCountryLabel}`}
                $labelAndBorderColor={labelAndBorderColor}
              >
                {selectedCountryLabel}
              </SelectedCountryLabel>
            )}
          </SelectedCountry>
          {selectedLanguageLabel && (
            <SelectedLanguageLabel
              data-testid={`languageCountrySwitchSelectedLanguage_${selectedLanguageLabel}`}
              $labelAndBorderColor={labelAndBorderColor}
            >
              {selectedLanguageLabel}
            </SelectedLanguageLabel>
          )}
        </>
      </Switch>
      {openAs === 'flyout' && (
        <LanguageCountrySwitchFlyout
          countries={countries}
          languages={languages}
          onClose={() => setFlyoutOpen(false)}
          open={flyoutOpen}
          selectedCountryFlagSrc={selectedCountryFlagSrc!}
          selectedCountryLabel={longSelectedCountryLabel!}
        />
      )}
      {shouldShowPopup && (
        <LanguageCountrySwitchPopupStyled
          $overlayWrapperZIndex={overlayWrapperZIndex}
          countries={countries}
          languages={languages}
          selectedCountryFlagSrc={selectedCountryFlagSrc!}
          selectedCountryLabel={longSelectedCountryLabel!}
        />
      )}
    </Wrapper>
  );
};

const ANMATION_PROPS = '0.3s ease-in-out';

const Switch = styled.button<{
  $shouldUseBorder: boolean;
}>`
  align-items: center;
  background-color: ${COLORS.Grey4};
  box-sizing: border-box;
  color: ${COLORS.Black};
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  outline: none;
  padding: 0 0.5rem;
  ${props =>
    props.$shouldUseBorder &&
    `
       border: 1px solid transparent;
       // eslint-disable-next-line local-rules/css-layout-animation
       transition: border ${ANMATION_PROPS};
  `}
`;

const Wrapper = styled.div<{
  $forceOpen: boolean;
  $popupEnabled: boolean;
  $shouldUseBorder: boolean;
  $shouldDisplayArrowOnHover?: boolean;
  $overlayWrapperZIndex?: number;
}>`
  cursor: pointer;
  position: relative;
  -webkit-tap-highlight-color: transparent;

  @media (hover: hover) {
    &:hover {
      ${Switch} {
        ${props =>
          props.$shouldUseBorder && `border: 1px solid ${COLORS.Black};`}
      }
      ${props => props.$shouldDisplayArrowOnHover && ArrowCss}
    }
  }

  ${({$forceOpen, $popupEnabled, $shouldUseBorder}) =>
    $forceOpen &&
    $popupEnabled &&
    $shouldUseBorder &&
    css`
      ${Switch} {
        border: 1px solid ${COLORS.Black};
      }
    `}
`;

const SelectedCountry = styled.div`
  align-items: center;
  display: flex;
`;

const SelectedCountryFlag = styled(CountryFlag)`
  margin-right: 0.25rem;
`;

export const SelectedCountryLabel = styled.span<{
  $labelAndBorderColor?: COLORS;
}>`
  font-size: ${FONT_SIZES.px10};
  font-weight: ${FONT_WEIGHTS.bold};
  line-height: 1;
  ${props =>
    props.$labelAndBorderColor && `color: ${props.$labelAndBorderColor};`}
`;

export const SelectedLanguageLabel = styled.span<{
  $labelAndBorderColor?: COLORS;
}>`
  border: 1px solid
    ${props =>
      props.$labelAndBorderColor
        ? `${props.$labelAndBorderColor}`
        : `${COLORS.Black}`};
  border-radius: 1px;
  box-sizing: border-box;
  color: ${props =>
    props.$labelAndBorderColor
      ? `${props.$labelAndBorderColor}`
      : `${COLORS.Black}`};
  display: block;
  font-size: ${FONT_SIZES.px8};
  line-height: 1;
  margin-left: 0.25rem;
  padding: 0.125rem;
`;

const LanguageCountrySwitchPopupStyled = styled(LanguageCountrySwitchPopup)<{
  $overlayWrapperZIndex?: number;
}>`
  ${props =>
    props.$overlayWrapperZIndex &&
    `z-index: ${props.$overlayWrapperZIndex} !important;
          `}
`;

const ArrowCss = css<{
  $overlayWrapperZIndex?: number;
}>`
  &:hover {
    // Pseudo-Element to hide the lower shadow of the arrow
    &::before {
      content: '';
      background-color: ${COLORS.White};
      width: 40px;
      height: 12px;
      position: absolute;
      bottom: -16px;
      left: 50%;
      transform: translateX(-50%);
      overflow: hidden;
      ${({$overlayWrapperZIndex}) =>
        $overlayWrapperZIndex &&
        `z-index: ${
          $overlayWrapperZIndex + 1 // +1 to ensure we are above the arrow to hide its bottom shadow
        };`}
    }
    // Pseudo-Element to display the arrow
    &::after {
      content: '';

      background: ${COLORS.White};
      border: 4px solid transparent;

      position: absolute;
      bottom: -4px;
      left: 45%;
      size: 8px;
      box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.2);

      transform: translateY(50%) rotate(45deg);

      ${({$overlayWrapperZIndex}) =>
        $overlayWrapperZIndex && `z-index: ${$overlayWrapperZIndex};`}
    }
  }
`;
